import React, { useState, useEffect } from 'react';

// 1. Go UP one level (../) to find the components
import HolsterVisualizer from '../HolsterVisualizer';
import ColorSelector from '../ColorSelector';
import OptionSelector from '../OptionSelector';
import SegmentedControl from '../SegmentedControl';
import useConfigShare from '../useConfigShare';
import ShareModal from '../ShareModal';

// --- NEW IMPORTS FOR REUSABLE COMPONENTS ---
import DisclaimerBox from '../DisclaimerBox'; 
import CollapsibleSection from '../CollapsibleSection';

// 2. Import Global Settings (UPDATED WITH LEAD TIME CONSTANTS)
import { 
  ENABLE_RUSH_SHIPPING, 
  RUSH_PRICE, 
  GLOBAL_DISCOUNT_PERCENT, 
  SALE_NAME,
  QS_DAYS, 
  RUSH_DAYS, 
  CUSTOM_DAYS,  
  QS_TIMELINE_TEXT, 
  RUSH_TIMELINE_TEXT, 
  CUSTOM_TIMELINE_TEXT,
  // Lead time labels + colors
  QS_LABEL, CUSTOM_LABEL, QS_COLOR, CUSTOM_COLOR, RUSH_COLOR, leadTimeTint,
  optionLine,
} from '../config/globalSettings';

// 3. Point to the REVOLVER data file
import { holsterData } from '../data/revolverData'; 

// 4. CSS
import '../App.css';

// ==========================================
// REVOLVER BUILDER COMPONENT
// ==========================================
function RevolverBuilder() {
  // --- STATE MANAGEMENT ---
  const [viewSide, setViewSide] = useState("front"); 
  const [handId, setHandId] = useState("right");
  const [brandId, setBrandId] = useState(""); 
  const [gunId, setGunId] = useState("");
  
  // REVOLVER SPECIFIC STATE
  const [opticId, setOpticId] = useState("no"); // Default to No Optic Cut
  
  const [colorId, setColorId] = useState("");
  const [mountId, setMountId] = useState("");
  const [clawId, setClawId] = useState(""); // One state for the single dropdown
  
  const [magId, setMagId] = useState(""); 
  const [breakthroughId, setBreakthroughId] = useState("");
  const [rushService, setRushService] = useState(false);

  // ==============================================================================
  // SHARE & SAVE FUNCTIONALITY
  // ==============================================================================
  const {
    getConfigFromURL,
    generateShareableURL,
    saveToLocalStorage,
    loadFromLocalStorage,
    savedConfigs,
    saveNamedConfig,
    deleteNamedConfig,
    copyToClipboard,
    copySuccess,
    shareModalOpen,
    setShareModalOpen,
  } = useConfigShare();

  const getCurrentConfig = () => ({
    handId, brandId, gunId, opticId, colorId, mountId, clawId, magId, breakthroughId, rushService,
  });

  const loadConfig = (config) => {
    if (!config) return;
    setHandId(config.handId || 'right');
    setBrandId(config.brandId || '');
    setGunId(config.gunId || '');
    setOpticId(config.opticId || 'no');
    setColorId(config.colorId || '');
    setMountId(config.mountId || '');
    setClawId(config.clawId || '');
    setMagId(config.magId || '');
    setBreakthroughId(config.breakthroughId || '');
    setRushService(config.rushService || false);
  };

  useEffect(() => {
    const urlConfig = getConfigFromURL();
    if (urlConfig) {
      loadConfig(urlConfig);
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    const savedConfig = loadFromLocalStorage();
    if (savedConfig) { loadConfig(savedConfig); }
  }, []); 

  useEffect(() => {
    if (gunId) {
      saveToLocalStorage(getCurrentConfig());
    }
  }, [handId, brandId, gunId, opticId, colorId, mountId, clawId, magId, breakthroughId, rushService]);

  const shareableURL = generateShareableURL(getCurrentConfig());

  // --- FILTERING ---
  const availableGuns = brandId 
    ? holsterData.guns.filter(g => g.brand_id === brandId)
    : [];

  // --- HELPERS ---
  const selectedGun     = holsterData.guns.find(g => g.id === gunId);
  const selectedOptic   = holsterData.optic_cut?.find(o => o.id === opticId); 
  const selectedColor   = holsterData.colors.find(c => c.id === colorId);
  const selectedMount   = holsterData.mounts.find(m => m.id === mountId);
  const selectedClaw    = holsterData.claws.find(c => c.id === clawId);
  const selectedMag     = holsterData.mag_carriers?.find(m => m.id === magId); 
  const selectedBreakthrough = holsterData.breakthrough.find(b => b.id === breakthroughId);

  // --- LEAD TIME LOGIC (REVOLVER SPECIFIC) ---
  const calculateLeadTime = () => {
    if (!selectedGun || !handId) return { text: "Select Options", date: "...", type: "PENDING" };

    const isQuickShip = selectedGun.quick_ship === true;

    const qsDays = QS_DAYS;       
    const rushDays = RUSH_DAYS;    
    const customDays = CUSTOM_DAYS;  
    
    let targetDays = customDays;
    if (isQuickShip) {
        targetDays = qsDays;
    } else if (rushService) {
        targetDays = rushDays;
    }
    
    const date = new Date();
    let daysAdded = 0;
    while (daysAdded < targetDays) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) daysAdded++;
    }
    const dateString = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    if (isQuickShip) {
        return { type: QS_LABEL, timeline: QS_TIMELINE_TEXT, date: dateString, color: QS_COLOR, bg: leadTimeTint(QS_COLOR), isCustom: false };
    } 
    if (rushService && ENABLE_RUSH_SHIPPING) {
        return { type: "RUSH PRIORITY", timeline: RUSH_TIMELINE_TEXT, date: dateString, color: RUSH_COLOR, bg: leadTimeTint(RUSH_COLOR), isCustom: true  };
    }
    return { type: CUSTOM_LABEL, timeline: CUSTOM_TIMELINE_TEXT, date: dateString, color: CUSTOM_COLOR, bg: leadTimeTint(CUSTOM_COLOR), isCustom: true  };
  };

  const leadTimeInfo = calculateLeadTime();

  // --- AUTO-CLEAR RUSH WHEN NOT APPLICABLE ---
  useEffect(() => {
      if (rushService && (!leadTimeInfo.isCustom || leadTimeInfo.type === QS_LABEL)) {
          setRushService(false);
      }
  }, [gunId, handId, leadTimeInfo]);

  // --- PRICING ---
  const BASE_PRICE = 69.99;

  const subtotal = 
    BASE_PRICE +
    (selectedOptic?.price      || 0) +
    (selectedColor?.price      || 0) +
    (selectedMount?.price      || 0) +
    (selectedClaw?.price       || 0) +
    (selectedMag?.price        || 0) +
    (selectedBreakthrough?.price || 0) +
    (rushService && ENABLE_RUSH_SHIPPING ? RUSH_PRICE : 0);

  const discountAmount = subtotal * GLOBAL_DISCOUNT_PERCENT;
  const total = (subtotal - discountAmount).toFixed(2);

  // --- VALIDATION ---
  const isReady = brandId && gunId && colorId && mountId && clawId && breakthroughId;

  // --- ADD TO CART ---
  const handleAddToCart = () => {
    if (!isReady) return;

    const configuration = [
        `Gun Model: ${selectedGun?.name}`,
        `Hand: ${handId === 'right' ? 'Right' : 'Left'}`,
        `Optic Cut: ${selectedOptic?.name || 'No'}`,
        `Color: ${selectedColor?.name}`,
        `Mount: ${selectedMount?.name}`,
        optionLine("Claw", selectedClaw?.name), 
        selectedMag ? `Mag Carrier: ${selectedMag.name}` : null,
        optionLine("Breakthrough Clean", selectedBreakthrough?.name),
        `Warranty: Free Lifetime Warranty`,
        `Lead Time: ${leadTimeInfo.timeline}`,
    ].filter(Boolean).join('\n');

    const productID = 283912;
    const params = new URLSearchParams();
    params.append('add-to-cart', productID);
    params.append('quantity', '1');
    params.append('anr_config', configuration);
    params.append('anr_price', total);

    window.location.href = `/checkout/?${params.toString()}`;
  };

  return (
    <div className={`builder-container ${colorId ? "sticky-active" : ""}`}>
      <div className="controls-section">
        <h1 className="header-title">Revolver IWB Builder <br/><span style={{fontSize:"16px", color:"#D5A678"}}>CUSTOM CONFIGURATION STUDIO</span></h1>
        
        {/* FEATURES */}
        <div style={{ color: "#aaa", fontSize: "14px", lineHeight: "1.7", marginBottom: "40px" }}>
           <h3 style={{ border: "none", padding: 0, color: "#fff", textTransform: "uppercase" }}>Features</h3>
           <ul style={{ paddingLeft: "20px", marginBottom: "20px", listStyleType: "disc" }}>
             <li><strong style={{ color: "#fff" }}>Minimalist Design:</strong> Low-profile carry experience.</li>
             <li><strong style={{ color: "#fff" }}>Full Trigger Guard Protection:</strong> Safe and secure.</li>
             <li><strong style={{ color: "#fff" }}>Perfect Retention:</strong> Keeps revolver securely in place.</li>
             <li><strong style={{ color: "#fff" }}>Durable KYDEX®:</strong> Handcrafted in the USA.</li>
             <li><strong style={{ color: "#fff" }}>Comfort Focused Design:</strong> Contoured edges and minimalist profile.</li>
           </ul>
        </div>

        {/* --- HAND SELECTION --- */}
        <SegmentedControl 
          label="Dominant Hand" 
          options={holsterData.hands} 
          selectedValue={handId} 
          onChange={(val) => { setHandId(val); setGunId(""); }} 
        />
        
        <OptionSelector label="Select Manufacturer *" options={holsterData.brands} selectedValue={brandId} onChange={(val) => { setBrandId(val); setGunId(""); }} />
        <OptionSelector label="Revolver Model *" options={availableGuns} selectedValue={gunId} onChange={setGunId} disabled={!brandId} />

        {/* --- S&W FITMENT INFO BOX --- */}
        {brandId === 'sw' && (
            <div style={{ backgroundColor: "#1e1e1e", borderLeft: "2px solid #D5A678", padding: "12px 15px", marginTop: "10px", fontSize: "13px", color: "#ccc", lineHeight: "1.6" }}>
                <div style={{ marginBottom: "6px" }}>
                    <strong style={{color: "#fff"}}>J-Frame fits:</strong> 642/442 Airweight, 340 PD, and other J-Frame revolvers. Available in 2″ and 3″ barrel lengths.
                </div>
                <div>
                    <strong style={{color: "#fff"}}>L-Frame fits:</strong> 586 L-Comp, Model 19, 686 Plus Deluxe, and other L/K-Frame revolvers. Available in 2.5″, 3″, 4″, and 5″ barrel lengths.
                </div>
            </div>
        )}

        {/* --- KIMBER FITMENT INFO BOX --- */}
        {brandId === 'kimber' && (
            <div style={{ backgroundColor: "#1e1e1e", borderLeft: "2px solid #D5A678", padding: "12px 15px", marginTop: "10px", fontSize: "13px", color: "#ccc", lineHeight: "1.6" }}>
                <div>
                    <strong style={{color: "#fff"}}>Kimber K6S:</strong> Available in 2″ and 3″ barrel configurations. Custom build — lead time 3–5 weeks.
                </div>
            </div>
        )}

        {/* --- RUGER FITMENT INFO BOX --- */}
        {brandId === 'ruger' && (
            <div style={{ backgroundColor: "#1e1e1e", borderLeft: "2px solid #D5A678", padding: "12px 15px", marginTop: "10px", fontSize: "13px", color: "#ccc", lineHeight: "1.6" }}>
                <div style={{ marginBottom: "6px" }}>
                    <strong style={{color: "#fff"}}>LCR .38:</strong> Fits the standard Ruger LCR .38 Special.
                </div>
                <div style={{ marginBottom: "6px" }}>
                    <strong style={{color: "#fff"}}>SP101 .38 SPL:</strong> Available in 2.25″ and 3″ barrel lengths.
                </div>
                <div>
                    <strong style={{color: "#fff"}}>GP100:</strong> Full-size Ruger GP100 revolvers.
                </div>
            </div>
        )}

        {/* --- TAURUS FITMENT INFO BOX --- */}
        {brandId === 'taurus' && (
            <div style={{ backgroundColor: "#1e1e1e", borderLeft: "2px solid #D5A678", padding: "12px 15px", marginTop: "10px", fontSize: "13px", color: "#ccc", lineHeight: "1.6" }}>
                <div style={{ marginBottom: "6px" }}>
                    <strong style={{color: "#fff"}}>Taurus 85 / 605:</strong> .38 SPL, 2″ barrel, 5-round cylinder.
                </div>
                <div>
                    <strong style={{color: "#fff"}}>Taurus 856:</strong> .38 SPL, 6-round cylinder.
                </div>
            </div>
        )}
        
        {/* OPTIC CUT SELECTOR */}
        {holsterData.optic_cut && (
            <OptionSelector 
                label="Optic Cut *" 
                options={holsterData.optic_cut} 
                selectedValue={opticId} 
                onChange={setOpticId} 
            />
        )}

        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>
        
        {/* COLORS SECTION */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "15px" }}>
          <h3 style={{ textTransform: "uppercase", margin: 0 }}>Color Options</h3>
          <div className="tooltip-wrapper">
             <span className="info-icon">i</span>
             <div className="tooltip-content">
                ANR Design does not have control over the pattern. The model portrayed in the configurator is a representation. 
             </div>
          </div>
        </div>
        <ColorSelector options={holsterData.colors} selectedValue={colorId} onChange={setColorId} />
        
        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>

        {/* MOUNTING */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "15px" }}>
          <h3 style={{ textTransform: "uppercase", margin: 0 }}>Mounting Options</h3>
          <div className="tooltip-wrapper">
              <span className="info-icon">i</span>
              <div className="tooltip-content mega-tooltip">
                  {holsterData.mounts.map(mount => (
                      <div key={mount.id} className="mount-info-row" style={{marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #555'}}>
                          <div className="mount-text">
                              <h4 style={{marginTop: 0, marginBottom: '5px'}}>{mount.name}</h4>
                              <p style={{margin: 0}}>{mount.description}</p>
                          </div>
                      </div>
                  ))}
              </div>
          </div>
        </div>
        <OptionSelector label="Mounting Selection *" options={holsterData.mounts} selectedValue={mountId} onChange={setMountId} />

        {/* CLAW SECTION */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "30px", marginBottom: "10px" }}>
           <h3 style={{ textTransform: "uppercase", margin: 0 }}>Concealment Claw</h3>
           <div className="tooltip-wrapper">
              <span className="info-icon">i</span>
              <div className="tooltip-content">
                  Injection molded accessory designed to reduce printing.
              </div>
           </div>
        </div>
        <OptionSelector label="Concealment Claw" options={holsterData.claws} selectedValue={clawId} onChange={setClawId} />

        {/* --- FREE LIFETIME WARRANTY BANNER --- */}
        <div style={{ marginTop: "30px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                <h3 style={{ textTransform: "uppercase", margin: 0 }}>Lifetime Warranty</h3>
                <div className="tooltip-wrapper">
                    <span className="info-icon">i</span>
                    <div className="tooltip-content">
                        Every ANR Design holster is backed by an automatic Lifetime Warranty on the Kydex® shell for the original purchaser. If your shell cracks or breaks under normal, intended use, we replace it. Consumables (hardware, loops, spacers), cosmetic wear, and damage from modifications or extreme heat are not covered. Return shipping fees apply.
                        <br/><br/>
                        <a href="https://www.anrkydexholsters.com/warranty-privacy-and-shipping-policy/" target="_blank" rel="noopener noreferrer" style={{ color: "#D5A678", textDecoration: "underline", fontWeight: "bold" }}>
                            Learn More & File a Claim
                        </a>
                    </div>
                </div>
            </div>
            <div style={{
                padding: "15px",
                backgroundColor: "rgba(76, 175, 80, 0.1)",
                borderLeft: "4px solid #4caf50",
                borderRadius: "4px",
                fontSize: "14px",
                color: "#4caf50",
                fontWeight: "600"
            }}>
                ✓ Free Lifetime Warranty Included
            </div>
        </div>

        {/* OPTIONAL EXTRAS */}
        {holsterData.mag_carriers && (
            <>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "30px", marginBottom: "15px" }}>
                    <h3 style={{ textTransform: "uppercase", margin: 0 }}>Add Matching Mag Carrier</h3>
                </div>
                <OptionSelector label="Add a Magazine Carrier *" options={holsterData.mag_carriers} selectedValue={magId} onChange={setMagId} />
            </>
        )}

        <h3 style={{ textTransform: "uppercase", marginTop: "30px" }}>Add Breakthrough Products</h3>
        <OptionSelector label="Optional add-on firearm maintenance" options={holsterData.breakthrough} selectedValue={breakthroughId} onChange={setBreakthroughId} />

        <hr style={{margin:"50px 0 20px 0", borderTop:"1px solid #333"}}/>

        {/* --- COLLAPSIBLE DESCRIPTION --- */}
        <CollapsibleSection title="Description">
            <p style={{ marginTop: 0 }}>
                Experience unmatched concealment and comfort with ANR Design's minimalist KYDEX® Thermoplastics IWB revolver holsters.
                These holsters are meticulously crafted to provide a secure and discreet carry solution for your revolver.
            </p>
            <ul style={{ paddingLeft: "20px", color: "#ccc", fontSize: "13px", lineHeight: "1.6" }}>
                <li><strong style={{color: "#fff"}}>S&W J-Frame:</strong> Fits 642/442 Airweight, 340 PD, and other J-Frame revolvers — 2″ and 3″ barrels.</li>
                <li><strong style={{color: "#fff"}}>S&W L-Frame:</strong> Fits 586, Model 19, 686 Plus Deluxe, and other L/K-Frame revolvers — 2.5″, 3″, 4″, and 5″ barrels.</li>
                <li><strong style={{color: "#fff"}}>Kimber K6S:</strong> 2″ and 3″ barrel lengths.</li>
                <li><strong style={{color: "#fff"}}>Ruger LCR .38, SP101 .38 SPL (2.25″ / 3″), GP100.</strong></li>
                <li><strong style={{color: "#fff"}}>Taurus 85/605 .38 SPL 2″ 5rd, 856 .38 SPL 6rd, Judge, Tracker.</strong></li>
            </ul>
        </CollapsibleSection>

        {/* COLLAPSIBLE WARNINGS */}
        <div className="mobile-only-content">
            <CollapsibleSection title="Important Notes & Warnings">
                <DisclaimerBox mode="mobile" setShareModalOpen={setShareModalOpen} gunId={gunId} />
            </CollapsibleSection>
        </div>

      </div>

      <div className="preview-section">
        <div className="sticky-wrapper">

          <HolsterVisualizer 
            holsterType="revolver" 
            gunData={selectedGun} 
            colorData={selectedColor} 
            mountData={selectedMount} 
            clawData={selectedClaw} 
            isLightbearing="no" 
            isLeftHanded={handId === "left"} 
            viewSide={viewSide} 
          />

          {/* --- FRONT / REAR TOGGLE --- */}
          <div style={{ display: "flex", justifyContent: "center", marginTop: "15px", marginBottom: "15px" }}>
            <div style={{ display: "flex", backgroundColor: "#333", borderRadius: "4px", padding: "4px", gap: "5px" }}>
              <button onClick={() => setViewSide("front")} style={{ background: viewSide === "front" ? "#D5A678" : "transparent", color: viewSide === "front" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>FRONT</button>
              <button onClick={() => setViewSide("back")} style={{ background: viewSide === "back" ? "#D5A678" : "transparent", color: viewSide === "back" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>REAR</button>
            </div>
          </div>

          {/* --- LEAD TIME BOX --- */}
          {leadTimeInfo.type !== "PENDING" && (
             <div className="lead-time-box" style={{ 
                 backgroundColor: leadTimeInfo.bg, 
                 padding: "15px", 
                 borderLeft: `4px solid ${leadTimeInfo.color}`, 
                 marginBottom: "20px",
                 transition: "all 0.3s ease"
             }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                  <div>
                      {leadTimeInfo.type && <strong style={{ color: leadTimeInfo.color, textTransform: "uppercase", fontSize: "16px" }}>{leadTimeInfo.type}</strong>}
                      <div style={{ marginTop: "5px", color: "#fff" }}>
                        Lead Time: <strong style={{ color: leadTimeInfo.color }}>{leadTimeInfo.timeline}</strong><br/>
                        Est. Ship Date: <strong>{leadTimeInfo.date}</strong>
                      </div>
                  </div>
                  {leadTimeInfo.isCustom && ENABLE_RUSH_SHIPPING && (
                      <div style={{ marginLeft: "15px" }}>
                          <label style={{ display: "flex", alignItems: "center", cursor: "pointer", background: rushService ? "#D5A678" : "transparent", border: "1px solid #d32f2f", padding: "12px 20px", borderRadius: "4px", transition: "all 0.2s" }}>
                              <input type="checkbox" checked={rushService} onChange={(e) => setRushService(e.target.checked)} style={{ marginRight: "8px", accentColor: "#000" }} />
                              <div style={{ lineHeight: "1.7" }}>
                                  <div style={{ fontSize: "15px", fontWeight: "bold", color: rushService ? "#000" : "#D5A678", textTransform: "uppercase" }}>RUSH MY BUILD</div>
                                  <div style={{ fontSize: "15px", color: rushService ? "#000" : "#ccc" }}>+${RUSH_PRICE.toFixed(2)}</div>
                              </div>
                          </label>
                      </div>
                  )}
              </div>
             </div>
          )}
          {leadTimeInfo.type === "PENDING" && (
            <div style={{ textAlign: "center", marginTop: "15px", color: "#666", fontSize: "12px", marginBottom: "15px" }}>Select options to see lead time</div>
          )}
          
          <div className="price-tag">
            {GLOBAL_DISCOUNT_PERCENT > 0 ? (
                <>
                <span style={{ textDecoration: "line-through", color: "#888", marginRight: "10px", fontSize: "0.8em" }}>${subtotal.toFixed(2)}</span>
                <span style={{ color: "#00ff00" }}>${total}</span>
                <div style={{ fontSize: "10px", color: "#00ff00", textTransform: "uppercase", marginTop: "5px" }}>{SALE_NAME} ACTIVE</div>
                </>
            ) : (
                `Total: $${total}`
            )}
          </div>

          {/* DESKTOP DISCLAIMERS & SHARE BUTTON */}
          <DisclaimerBox mode="desktop" setShareModalOpen={setShareModalOpen} gunId={gunId} />

          <button className={`add-to-cart-btn ${isReady ? "pulse-active" : ""}`} disabled={!isReady} onClick={handleAddToCart}>
            {isReady ? "ADD TO CART" : "SELECT OPTIONS"}
          </button>
        </div>
      </div>
      
      <div className="mobile-action-bar mobile-only-content">
        <div className="mobile-price-container">
          <span className="mobile-label">Estimated Total</span>
          <span className="mobile-total">${total}</span>
        </div>
        <button className={`mobile-cart-btn ${isReady ? "pulse-active" : ""}`} disabled={!isReady} onClick={handleAddToCart}>
           {isReady ? "ADD TO CART" : "INCOMPLETE"}
        </button>
      </div>

      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        shareableURL={shareableURL}
        currentConfig={getCurrentConfig()}
        savedConfigs={savedConfigs}
        onSaveConfig={saveNamedConfig}
        loadConfig={loadConfig}
        onDeleteConfig={deleteNamedConfig}
        onCopyLink={copyToClipboard}
        copySuccess={copySuccess}
        gunName={selectedGun?.name}
        colorName={selectedColor?.name}
      />
    </div>
  );
}

export default RevolverBuilder;